// The booking calendar's view state as it travels in the URL, so opening a
// booking and coming back lands on the same week.

export const CALENDAR_VIEWS = ['fleet', 'week', 'tenDay', 'month', 'agenda'] as const
export type CalendarViewName = (typeof CALENDAR_VIEWS)[number]

export const CALENDAR_PATH = '/bookings/calendar'

export interface CalendarState {
  view: CalendarViewName | null
  date: Date | null
  aircraft: string | null
}

export function isCalendarView(value: unknown): value is CalendarViewName {
  return typeof value === 'string' && (CALENDAR_VIEWS as readonly string[]).includes(value)
}

// Local calendar date, YYYY-MM-DD: the member's day, not UTC's.
export function formatCalendarDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Noon, so a DST shift cannot move it to another day. Null for anything
// that is not a real date (2026-02-31 included).
export function parseCalendarDate(value: string | null): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '')
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day, 12)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
}

export function parseCalendarState(params: { get(name: string): string | null }): CalendarState {
  const view = params.get('view')
  const aircraft = params.get('aircraft')
  return {
    view: isCalendarView(view) ? view : null,
    date: parseCalendarDate(params.get('date')),
    aircraft: aircraft && /^[A-Za-z0-9-]{1,10}$/.test(aircraft) ? aircraft : null,
  }
}

export function calendarSearch(state: { view: CalendarViewName; date: Date; aircraft: string | null }): Record<string, string> {
  const params: Record<string, string> = { view: state.view, date: formatCalendarDate(state.date) }
  if (state.aircraft) params.aircraft = state.aircraft
  return params
}

// Where a form may send the member back to. The path arrives in router
// state, so accept only an in-app booking path, never a URL elsewhere.
export function safeReturnPath(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (!value.startsWith('/bookings') || value.startsWith('//') || value.includes('\\')) return null
  return value === '/bookings' || value.startsWith('/bookings/') || value.startsWith('/bookings?') ? value : null
}

// Days the rolling view shows, counting the first.
export const TEN_DAY_SPAN = 10
